import React, { useState } from 'react';
import { MessageSquare, Send, MinimizeIcon, Maximize2Icon, ShieldAlert, FileText, ArrowRight, AlertTriangle, Info, Sparkles, PieChart, BookOpen, Calendar, Lightbulb, Maximize } from 'lucide-react';
const regulatoryChanges = [{
  id: 1,
  title: 'FDA Phasing Out Red Food Coloring #3',
  description: 'The FDA has announced a phase-out of Red Food Coloring #3 by 2026 due to potential health concerns.',
  authority: 'FDA',
  effectiveDate: 'January 2026',
  impact: 'high',
  affectedProducts: 8,
  status: 'upcoming'
}, {
  id: 2,
  title: 'New Organic Certification Requirements',
  description: 'USDA implementing stricter requirements for organic certification, affecting ingredient sourcing.',
  authority: 'USDA',
  effectiveDate: 'March 2025',
  impact: 'medium',
  affectedProducts: 12,
  status: 'upcoming'
}, {
  id: 3,
  title: 'Allergen Labeling Changes',
  description: 'FDA requiring more prominent allergen warnings and additional allergens to be listed.',
  authority: 'FDA',
  effectiveDate: 'June 2024',
  impact: 'high',
  affectedProducts: 23,
  status: 'urgent'
}];
const suggestedQuestions = [{
  category: 'Regulatory Updates',
  questions: ['What are the latest FDA regulatory changes?', 'Explain the Red Food Coloring #3 phase-out timeline', 'What new allergen labeling requirements are coming?']
}, {
  category: 'Compliance Strategy',
  questions: ['How should we prepare for organic certification changes?', 'What alternatives exist for Red Food Coloring #3?', 'Steps to update our allergen labeling']
}, {
  category: 'Risk Assessment',
  questions: ['Which of our products are most at risk?', "What's our current compliance score breakdown?", 'Compare our compliance to industry benchmarks']
}];
export function ComplianceAssistant() {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [expandedChange, setExpandedChange] = useState<number | null>(null);
  const [activeQuestionCategory, setActiveQuestionCategory] = useState('Regulatory Updates');
  const getImpactBadge = (impact: string) => {
    switch (impact) {
      case 'high':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'medium':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'low':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'urgent':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'upcoming':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'active':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };
  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Regulatory Updates':
        return <BookOpen className="w-4 h-4" data-id="element-90" />;
      case 'Compliance Strategy':
        return <PieChart className="w-4 h-4" data-id="element-91" />;
      case 'Risk Assessment':
        return <AlertTriangle className="w-4 h-4" data-id="element-92" />;
      default:
        return <Info className="w-4 h-4" data-id="element-93" />;
    }
  };
  return <div className={`bg-white border rounded-lg mb-8 transition-all duration-300 ${isCollapsed ? 'h-16' : isExpanded ? 'min-h-[800px]' : ''}`} data-id="element-94">
      <div className="p-6" data-id="element-95">
        <div className="flex items-center justify-between mb-6" data-id="element-96">
          <div className="flex items-center space-x-3" data-id="element-97">
            <div className="bg-purple-100 p-2 rounded-lg" data-id="element-98">
              <ShieldAlert className="w-6 h-6 text-purple-600" data-id="element-99" />
            </div>
            <div data-id="element-100">
              <h2 className="font-semibold text-xl text-gray-900" data-id="element-101">
                Compliance Assistant
              </h2>
              <p className="text-sm text-gray-600" data-id="element-102">
                Stay updated on regulatory changes affecting your products
              </p>
            </div>
          </div>
          <div className="flex space-x-2" data-id="element-103">
            {!isCollapsed && <button onClick={() => setIsExpanded(!isExpanded)} className="text-gray-500 hover:text-gray-700" data-id="element-104">
                {isExpanded ? <MinimizeIcon className="w-5 h-5" data-id="element-105" /> : <Maximize className="w-5 h-5" data-id="element-106" />}
              </button>}
            <button onClick={() => setIsCollapsed(!isCollapsed)} className="text-gray-500 hover:text-gray-700" data-id="element-107">
              {isCollapsed ? <Maximize2Icon className="w-5 h-5" data-id="element-108" /> : <MinimizeIcon className="w-5 h-5" data-id="element-109" />}
            </button>
          </div>
        </div>
        {!isCollapsed && <>
            <div className={`mb-6 ${isExpanded ? 'max-w-4xl mx-auto' : ''}`} data-id="element-110">
              {isExpanded && <div className="mb-6 bg-purple-50 p-4 rounded-lg border border-purple-100" data-id="element-111">
                  <div className="flex items-center space-x-2 mb-3" data-id="element-112">
                    <Lightbulb className="w-5 h-5 text-purple-600" data-id="element-113" />
                    <h3 className="font-medium text-purple-800" data-id="element-114">
                      Ask me about compliance
                    </h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-115">
                    {suggestedQuestions.map(category => <div key={category.category} className={`p-3 rounded-lg border ${activeQuestionCategory === category.category ? 'bg-white border-purple-300' : 'bg-white border-gray-200 hover:border-purple-200'}`} data-id="element-116">
                        <button className="w-full text-left" onClick={() => setActiveQuestionCategory(category.category)} data-id="element-117">
                          <div className="flex items-center space-x-2 mb-2" data-id="element-118">
                            {getCategoryIcon(category.category)}
                            <h4 className="text-sm font-medium text-gray-900" data-id="element-119">
                              {category.category}
                            </h4>
                          </div>
                        </button>
                      </div>)}
                  </div>
                </div>}
              {suggestedQuestions.find(c => c.category === activeQuestionCategory) && <div className="mb-4" data-id="element-120">
                  <div className="flex flex-wrap gap-2" data-id="element-121">
                    {suggestedQuestions.find(c => c.category === activeQuestionCategory)?.questions.map((question, index) => <button key={index} className="px-3 py-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg text-sm text-gray-700" onClick={() => setInputValue(question)} data-id="element-122">
                          {question}
                        </button>)}
                  </div>
                </div>}
              <div className="relative" data-id="element-123">
                <input type="text" placeholder="Search regulatory changes or ask about compliance..." value={inputValue} onChange={e => setInputValue(e.target.value)} className="w-full px-4 py-3 pr-10 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent" data-id="element-124" />
                <button className="absolute right-3 top-1/2 transform -translate-y-1/2 text-purple-600 hover:text-purple-700" data-id="element-125">
                  <Send className="w-5 h-5" data-id="element-126" />
                </button>
              </div>
            </div>
            <div className={`mb-6 ${isExpanded ? 'max-w-4xl mx-auto' : ''}`} data-id="element-127">
              <div className="flex items-center justify-between mb-4" data-id="element-128">
                <h3 className="font-medium text-gray-900" data-id="element-129">
                  Regulatory Changes
                </h3>
                <div className="flex space-x-2" data-id="element-130">
                  <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800" data-id="element-131">
                    1 Urgent
                  </span>
                  <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800" data-id="element-132">
                    2 Upcoming
                  </span>
                </div>
              </div>
              <div className="space-y-4" data-id="element-133">
                {regulatoryChanges.map(change => <div key={change.id} className="border rounded-lg overflow-hidden" data-id="element-134">
                    <div className="p-4 cursor-pointer hover:bg-gray-50" onClick={() => setExpandedChange(expandedChange === change.id ? null : change.id)} data-id="element-135">
                      <div className="flex justify-between items-start" data-id="element-136">
                        <div className="flex items-start space-x-3" data-id="element-137">
                          {change.status === 'urgent' ? <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" data-id="element-138" /> : <Info className="w-5 h-5 text-blue-600 mt-0.5" data-id="element-139" />}
                          <div data-id="element-140">
                            <h4 className="font-medium text-gray-900" data-id="element-141">
                              {change.title}
                            </h4>
                            <p className="text-sm text-gray-600 mt-1" data-id="element-142">
                              {change.description}
                            </p>
                            <div className="flex items-center space-x-3 mt-2" data-id="element-143">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getImpactBadge(change.impact)}`} data-id="element-144">
                                {change.impact.charAt(0).toUpperCase() + change.impact.slice(1)}{' '}
                                Impact
                              </span>
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStatusBadge(change.status)}`} data-id="element-145">
                                {change.status.charAt(0).toUpperCase() + change.status.slice(1)}
                              </span>
                              <span className="text-xs text-gray-500" data-id="element-146">
                                Effective: {change.effectiveDate}
                              </span>
                            </div>
                          </div>
                        </div>
                        <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-medium" data-id="element-147">
                          {change.affectedProducts} Products Affected
                        </span>
                      </div>
                    </div>
                    {expandedChange === change.id && <div className="p-4 bg-gray-50 border-t" data-id="element-148">
                        <h5 className="font-medium text-gray-900 mb-3" data-id="element-149">
                          Affected Products
                        </h5>
                        <div className="space-y-2" data-id="element-150">
                          {change.id === 1 && <>
                              <div className="flex items-center justify-between bg-white rounded p-3" data-id="element-151">
                                <div className="flex items-center space-x-3" data-id="element-152">
                                  <img src="https://images.unsplash.com/photo-1560963689-b5682b6440f8?w=60&h=60&fit=crop&crop=center" alt="Product" className="w-10 h-10 rounded-lg object-cover" data-id="element-153" />
                                  <div data-id="element-154">
                                    <p className="text-sm font-medium text-gray-900" data-id="element-155">
                                      Organic Granola Bars - Chocolate Chip
                                    </p>
                                    <p className="text-xs text-gray-600" data-id="element-156">
                                      Contains Red Food Coloring #3
                                    </p>
                                  </div>
                                </div>
                                <button className="text-purple-600 hover:text-purple-700 text-sm font-medium" data-id="element-157">
                                  View Details
                                </button>
                              </div>
                              <div className="flex items-center justify-between bg-white rounded p-3" data-id="element-158">
                                <div className="flex items-center space-x-3" data-id="element-159">
                                  <img src="https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=60&h=60&fit=crop&crop=center" alt="Product" className="w-10 h-10 rounded-lg object-cover" data-id="element-160" />
                                  <div data-id="element-161">
                                    <p className="text-sm font-medium text-gray-900" data-id="element-162">
                                      Protein Shake Mix - Vanilla
                                    </p>
                                    <p className="text-xs text-gray-600" data-id="element-163">
                                      Contains Red Food Coloring #3
                                    </p>
                                  </div>
                                </div>
                                <button className="text-purple-600 hover:text-purple-700 text-sm font-medium" data-id="element-164">
                                  View Details
                                </button>
                              </div>
                            </>}
                          {change.id !== 1 && <p className="text-sm text-gray-600 py-2" data-id="element-165">
                              Showing 2 of {change.affectedProducts} affected
                              products
                            </p>}
                        </div>
                        <div className="mt-4 flex justify-between" data-id="element-166">
                          <button className="text-purple-600 hover:text-purple-700 text-sm font-medium flex items-center" data-id="element-167">
                            <span data-id="element-168">View All Affected Products</span>
                            <ArrowRight className="w-4 h-4 ml-1" data-id="element-169" />
                          </button>
                          <button className="text-purple-600 hover:text-purple-700 text-sm font-medium flex items-center" data-id="element-170">
                            <FileText className="w-4 h-4 mr-1" data-id="element-171" />
                            <span data-id="element-172">Regulatory Document</span>
                          </button>
                        </div>
                      </div>}
                  </div>)}
              </div>
            </div>
            {isExpanded && <div className={`mb-6 ${isExpanded ? 'max-w-4xl mx-auto' : ''}`} data-id="element-173">
                <div className="bg-gray-50 border rounded-lg p-4" data-id="element-174">
                  <div className="flex items-center space-x-2 mb-3" data-id="element-175">
                    <Sparkles className="w-5 h-5 text-purple-600" data-id="element-176" />
                    <h3 className="font-medium text-gray-900" data-id="element-177">
                      AI Assistant Capabilities
                    </h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-178">
                    <div className="bg-white p-4 rounded-lg border" data-id="element-179">
                      <h4 className="font-medium text-gray-900 mb-2" data-id="element-180">
                        Regulatory Analysis
                      </h4>
                      <p className="text-sm text-gray-600" data-id="element-181">
                        Ask about specific regulations, compliance deadlines,
                        and how they affect your products.
                      </p>
                    </div>
                    <div className="bg-white p-4 rounded-lg border" data-id="element-182">
                      <h4 className="font-medium text-gray-900 mb-2" data-id="element-183">
                        Compliance Strategy
                      </h4>
                      <p className="text-sm text-gray-600" data-id="element-184">
                        Get recommendations for addressing compliance issues and
                        preparing for upcoming changes.
                      </p>
                    </div>
                    <div className="bg-white p-4 rounded-lg border" data-id="element-185">
                      <h4 className="font-medium text-gray-900 mb-2" data-id="element-186">
                        Impact Assessment
                      </h4>
                      <p className="text-sm text-gray-600" data-id="element-187">
                        Evaluate how regulatory changes might impact your
                        product portfolio and supply chain.
                      </p>
                    </div>
                  </div>
                </div>
              </div>}
            <div className={`${isExpanded ? 'max-w-4xl mx-auto' : ''}`} data-id="element-188">
              <div className="flex items-center justify-between mb-4" data-id="element-189">
                <h3 className="font-medium text-gray-900" data-id="element-190">
                  Compliance Actions
                </h3>
                <button className="text-sm text-purple-600 hover:text-purple-700" data-id="element-191">
                  See all
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-192">
                <div className="border rounded-lg p-4 hover:border-purple-300 cursor-pointer transition-colors" data-id="element-193">
                  <div className="bg-purple-50 w-10 h-10 rounded-lg flex items-center justify-center mb-3" data-id="element-194">
                    <FileText className="w-5 h-5 text-purple-600" data-id="element-195" />
                  </div>
                  <h4 className="font-medium mb-1" data-id="element-196">
                    Generate Compliance Report
                  </h4>
                  <p className="text-sm text-gray-600 mb-3" data-id="element-197">
                    Create a comprehensive report of all compliance issues
                  </p>
                  <div className="flex items-center text-sm text-gray-500" data-id="element-198">
                    <span data-id="element-199">1 step</span>
                    <ArrowRight className="w-4 h-4 ml-2" data-id="element-200" />
                  </div>
                </div>
                <div className="border rounded-lg p-4 hover:border-purple-300 cursor-pointer transition-colors" data-id="element-201">
                  <div className="bg-purple-50 w-10 h-10 rounded-lg flex items-center justify-center mb-3" data-id="element-202">
                    <ShieldAlert className="w-5 h-5 text-purple-600" data-id="element-203" />
                  </div>
                  <h4 className="font-medium mb-1" data-id="element-204">
                    Regulatory Impact Analysis
                  </h4>
                  <p className="text-sm text-gray-600 mb-3" data-id="element-205">
                    Assess the impact of new regulations on your products
                  </p>
                  <div className="flex items-center text-sm text-gray-500" data-id="element-206">
                    <span data-id="element-207">2 steps</span>
                    <ArrowRight className="w-4 h-4 ml-2" data-id="element-208" />
                  </div>
                </div>
              </div>
            </div>
          </>}
      </div>
    </div>;
}